<template>
  <UIDropdown
    class="TheSidebarUserMenu"
    :items="userOptions"
    placement="right-end"
  >
    <template #toggle-container>
      <button class="TheSidebarUserMenu__toggle" type="button">
        <UIAvatar
          class="TheSidebarUserMenu__avatar"
          :fullname="fullName"
          size="md"
          theme="green"
        />

        <span class="TheSidebarUserMenu__account">
          <span class="TheSidebarUserMenu__name" :title="fullName">
            {{ fullName }}
          </span>
          <!-- Native `title`, not ui-lib UITooltip: its trigger span stops click
               propagation, so clicks on the email never reached the dropdown toggle. -->
          <span
            v-if="user?.email"
            class="TheSidebarUserMenu__email"
            :title="user.email"
          >
            {{ user.email }}
          </span>
        </span>
      </button>
    </template>
  </UIDropdown>

  <LogoutModal
    v-bind="logoutModal"
    :tenant="logoutModal.modalData"
    @close="logoutModal.close()"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { UIAvatar, UIDropdown } from '@foundever/ui-lib'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'

import { useColorScheme } from '@/composables/useColorScheme'
import { useModal } from '@/composables/useModal'
import { usePersonalisationModal } from '@/composables/usePersonalisationModal'
import { useAuthStore } from '@/store/auth'
import LogoutModal from '@/views/pages/Auth/Logout/LogoutModal.vue'

const authStore = useAuthStore()
const { user, isPlarformAdmin } = storeToRefs(authStore)
const router = useRouter()
const personalisationModal = usePersonalisationModal()
const logoutModal = useModal()
const { isDark, toggleColorScheme } = useColorScheme()

const fullName = computed(() => user.value?.name || '-')

const logOut = () => {
  logoutModal.open()
  window.setTimeout(() => {
    authStore.logout()
  }, 400)
}

const userOptions = computed(() => [
  ...(isPlarformAdmin.value
    ? [
        {
          label: 'Admin',
          value: 'Admin',
          onClick: () => {
            router.push('/admin')
          },
        },
      ]
    : []),
  {
    label: 'Personalization',
    value: 'Personalization',
    onClick: () => {
      personalisationModal.open()
    },
  },
  {
    label: isDark.value ? 'Light mode' : 'Dark mode',
    value: 'ColorScheme',
    onClick: toggleColorScheme,
  },
  {
    label: 'Sign out',
    value: 'Logout',
    onClick: logOut,
  },
])
</script>

<style scoped lang="scss">
.TheSidebarUserMenu {
  min-width: 0;
  width: 100%;

  :deep(> *) {
    min-width: 0;
    width: 100%;
  }

  .TheSidebarUserMenu__toggle {
    border: 0;
    background: transparent;
    width: 100%;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.25rem;
    border-radius: var(--radius-xs);
    cursor: pointer;
    text-align: left;

    &:hover {
      background-color: var(--interactive-bg-tertiary-hover);
    }

    &:active {
      background-color: var(--interactive-bg-accent-press);
    }
  }

  .TheSidebarUserMenu__avatar {
    flex-shrink: 0;
  }

  .TheSidebarUserMenu__account {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    justify-content: center;
    line-height: 1rem;
  }

  .TheSidebarUserMenu__name,
  .TheSidebarUserMenu__email {
    display: block;
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .TheSidebarUserMenu__name {
    @include font-caption-bold;
    color: var(--text-primary);
  }

  .TheSidebarUserMenu__email {
    @include font-caption-regular;
    color: var(--text-secondary);
  }
}
</style>
